import { Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MediumEditorSharedComponent } from './../edit-sidenav/medium-editor-shared/medium-editor-shared.component';
import { ICustomerCare } from '../../../core/interfaces/customer-care.interface';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SetComponentsService } from '../../../services/edit/set-components.service';
import { SharedService } from '../../../services/shared/shared.service';
import { LayoutService } from '../../../services/layout.service';
import { SidenavContent } from '../../side-nav/sidenav-content';

@Component({
  selector: 'edit-customer-care',
  templateUrl: './edit-customer-care.component.html',
  styleUrls: ['./edit-customer-care.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditCustomerCareComponent extends SidenavContent implements OnInit {
  @ViewChild('mediumEditor') mediumEditor: MediumEditorSharedComponent;
  @Input() typeOf: string;
  @Input() rowType: string;
  @Input() rowArray: number;
  @Input() compArray: number;
  @Input() edit: boolean;
  @Input() template: ICustomerCare[];

  customerCareDetails: ICustomerCare;
  customerCareDetailsRef: ICustomerCare;
  editCustomerCareForm: UntypedFormGroup;
  custHeaderMaxCharStr = '500 characters maximum';
  maxCharStr = '100 characters maximum';

  private _urlPattern = '(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?';
  private _phoneNumPattern = '^((\\+91-?)|0)?[0-9]{10}$';
  private _emailPattern = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[.][a-zA-Z]{2,4}$';

  constructor(
    private _fb: UntypedFormBuilder,
    private _layoutService: LayoutService,
    private _setComponentService: SetComponentsService,
    private _sharedService: SharedService,
  ) {
    super(_layoutService);
  }

  ngOnInit(): void {
    if (this.template && this.template[0] && this.edit) {
      {
        this.customerCareDetails = this.template[0];
        this.customerCareDetailsRef = JSON.parse(JSON.stringify(this.customerCareDetails)); // deep copy
        this._createEditCustomerCareForm();
      }
    }
  }

  private _createEditCustomerCareForm(): void {
    this.editCustomerCareForm = this._fb.group({
      customerCareHeaderHtml: [
        this.customerCareDetails?.customerCareHeaderHtml.trim(),
        [Validators.required],
      ],

      customerPortalText: [
        this.customerCareDetails?.customerPortalText.trim(),
        [Validators.required, Validators.maxLength(100)],
      ],
      customerPortalLinkLabel: [
        this.customerCareDetails?.customerPortalLinkLabel.trim(),
        [Validators.required, Validators.maxLength(100)],
      ],
      customerPortalLink: [
        this.customerCareDetails?.customerPortalLink.trim(),
        [Validators.required, Validators.pattern(this._urlPattern)],
      ],
      isCustomerPortal: [this.customerCareDetails?.isCustomerPortal],

      phoneNumber: [
        this.customerCareDetails?.phoneNumber.trim(),
        [Validators.required, Validators.pattern(this._phoneNumPattern)],
      ],
      emailAddressText: [
        this.customerCareDetails?.emailAddressText.trim(),
        [Validators.required, Validators.maxLength(100)],
      ],
      toEmailAddressOnSubmit: [
        this.customerCareDetails?.toEmailAddressOnSubmit.trim(),
        [Validators.required, Validators.pattern(this._emailPattern)],
      ],
      emailAddress: [
        this.customerCareDetails?.emailAddress.trim(),
        [Validators.pattern(this._emailPattern)],
      ],
      timingsText: [
        this.customerCareDetails?.timingsText.trim(),
        [Validators.required, Validators.maxLength(100)],
      ],

      customerSurveyText: [
        this.customerCareDetails?.customerSurveyText.trim(),
        [Validators.required, Validators.maxLength(100)],
      ],
      customerSurveyLinkLabel: [
        this.customerCareDetails?.customerSurveyLinkLabel.trim(),
        [Validators.required, Validators.maxLength(100)],
      ],
      customerSurveyLink: [
        this.customerCareDetails?.customerSurveyLink.trim(),
        [Validators.required, Validators.pattern(this._urlPattern)],
      ],
      isCustomerSurvey: [this.customerCareDetails?.isCustomerPortal],
    });
  }

  get f() {
    return this.editCustomerCareForm.controls;
  }

  get customerPortalLink() {
    return this.editCustomerCareForm.get('customerPortalLink');
  }

  get customerPortalText() {
    return this.editCustomerCareForm.get('customerPortalText');
  }

  get customerPortalLinkLabel() {
    return this.editCustomerCareForm.get('customerPortalLinkLabel');
  }

  get emailAddressText() {
    return this.editCustomerCareForm.get('emailAddressText');
  }

  get timingsText() {
    return this.editCustomerCareForm.get('timingsText');
  }

  get customerSurveyLink() {
    return this.editCustomerCareForm.get('customerSurveyLink');
  }

  get customerSurveyText() {
    return this.editCustomerCareForm.get('customerSurveyText');
  }

  get customerSurveyLinkLabel() {
    return this.editCustomerCareForm.get('customerSurveyLinkLabel');
  }

  get phoneNumber() {
    return this.editCustomerCareForm.get('phoneNumber');
  }

  get toEmailAddressOnSubmit() {
    return this.editCustomerCareForm.get('toEmailAddressOnSubmit');
  }

  get emailAddress() {
    return this.editCustomerCareForm.get('emailAddress');
  }

  submitCustomerCareForm(): void {
    if (this.mediumEditor.medEditElRef.nativeElement.innerHTML.length > 500) {
      this._sharedService.openSnackBar(
        `${this.custHeaderMaxCharStr} allowed for customer care header text`,
      );
      return;
    }

    this.closeNav();
    this._setComponentService.setHasCustomerCareFormChanges(false);

    if (this.mediumEditor.medEditElRef.nativeElement.innerHTML != '') {
      // remove empty paragraph added by medium-editor
      this.mediumEditor.medEditElRef.nativeElement.innerHTML =
        this.mediumEditor.medEditElRef.nativeElement.innerHTML.replace('<p></p>', '');
    }

    if (
      !this.editCustomerCareForm.touched &&
      this.customerCareDetailsRef.customerCareHeaderHtml ===
        this.mediumEditor.medEditElRef.nativeElement.innerHTML
    ) {
      return;
    }

    if (
      this.editCustomerCareForm.pristine &&
      this.customerCareDetailsRef.customerCareHeaderHtml ===
        this.mediumEditor.medEditElRef.nativeElement.innerHTML
    ) {
      return;
    }

    let hasChanges = false;
    hasChanges =
      this.customerCareDetailsRef.customerCareHeaderHtml !==
      this.mediumEditor.medEditElRef.nativeElement.innerHTML;
    for (const prop in this.editCustomerCareForm.value) {
      if (this.isDifferent(this.editCustomerCareForm.value, prop)) {
        hasChanges = true;
      }
    }

    if (!hasChanges) {
      return;
    }

    this._setComponentService.setHasCustomerCareFormChanges(true);
    this.updateCustomerCare();
  }

  // Returns true if the user has changed the value in the form
  isDifferent(obj: any, prop: string) {
    return this.customerCareDetailsRef[prop] !== obj[prop];
  }

  updateCustomerCare(): void {
    const ccHeaderHtml = this.mediumEditor.medEditElRef.nativeElement.innerHTML;
    this.customerCareDetails.customerCareHeaderHtml = ccHeaderHtml;
    this.editCustomerCareForm.controls['customerCareHeaderHtml'].setValue(ccHeaderHtml);
    this._setComponentService.setCustomerCare(this.editCustomerCareForm.value);
  }

  onReset(): void {
    this.editCustomerCareForm.reset();
  }
}
